import { apiClient } from "./apiClient";

/**
 * Per-placeholder binding stored on the template row.
 * - `path` is either a single dot-path or an ordered fallback chain.
 * - `fallback` is a literal used only when every path resolves to null/blank.
 */
export interface TemplateParamMapping {
  path: string | string[];
  fallback?: string;
}

export interface WhatsappTemplate {
  id: string;
  templateName: string;
  templateBody?: string | null;
  language: string;
  branchId: string;
  legalEntityId?: string | null;
  waStatus?: string | null;
  waCategory?: string | null;
  footer?: string | null;
  /** Stored per-template binding; null → manual entry only. */
  paramMappings?: TemplateParamMapping[] | null;
}

/**
 * Hydrated reach-out context fed into the param resolver. Each field is
 * optional so partially-filled forms still produce a partial pre-fill.
 */
export interface ReachoutResolverContext {
  contact?: Record<string, unknown> | null;
  legalEntity?: Record<string, unknown> | null;
  branch?: Record<string, unknown> | null;
  property?: Record<string, unknown> | null;
  appointment?: Record<string, unknown> | null;
}

/** Bare array or a paged/envelope payload — the listing has been seen as both. */
function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readString(row: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value == null || value === "") continue;
    return String(value).trim();
  }
  return "";
}

function normalizeTemplate(raw: unknown): WhatsappTemplate | null {
  const row = asRecord(raw);
  if (!row) return null;
  const id = readString(row, "id", "templateId", "template_id");
  if (!id) return null;
  return {
    id,
    templateName: readString(row, "templateName", "template_name", "name", "waTemplateName"),
    templateBody: readString(row, "templateBody", "template_body", "body") || null,
    language: readString(row, "language"),
    branchId: readString(row, "branchId", "branch_id"),
    legalEntityId: readString(row, "legalEntityId", "legal_entity_id") || null,
    waStatus: readString(row, "waStatus", "wa_status", "status") || null,
    waCategory: readString(row, "waCategory", "wa_category", "category") || null,
    footer: readString(row, "footer") || null,
    paramMappings: (row.paramMappings ?? row.param_mappings) as TemplateParamMapping[] | null,
  };
}

function templateRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  const envelope = asRecord(data);
  if (!envelope) return [];
  for (const key of ["content", "items", "data", "templates", "results"]) {
    const value = envelope[key];
    if (Array.isArray(value)) return value;
    const nested = asRecord(value);
    if (!nested) continue;
    for (const nestedKey of ["content", "items", "templates"]) {
      if (Array.isArray(nested[nestedKey])) return nested[nestedKey] as unknown[];
    }
  }
  return [];
}

function templatesFromResponse(data: unknown): WhatsappTemplate[] {
  return templateRows(data)
    .map(normalizeTemplate)
    .filter((row): row is WhatsappTemplate => row != null);
}

export const whatsappTemplateService = {
  /**
   * Templates by id — `POST /api/ui/v1/templates/list`. Resolves a batch of ids
   * into names in one round trip; the response has been seen as a bare array
   * and as a paged envelope, so both are accepted.
   */
  listByIds: (ids: string[]): Promise<WhatsappTemplate[]> => {
    if (ids.length === 0) return Promise.resolve([]);
    return apiClient
      .post<WhatsappTemplate[] | { content?: WhatsappTemplate[] }>("/templates/list", { ids })
      .then((r) => templatesFromResponse(r.data));
  },

  /**
   * `GET /api/ui/v1/whatsapp/templates?legalEntityId=&branchId=`
   *
   * Params are sent only when present. "All legal entities" omits `legalEntityId`
   * so the endpoint returns every template the caller can see.
   * `skipGlobalContext` is stripped by the axios interceptor so the header's
   * entity/branch are not injected in their place.
   */
  list: (legalEntityId?: string | null, branchId?: string | null) => {
    const entity = legalEntityId?.trim() || "";
    const branch = branchId?.trim() || "";
    return apiClient
      .get("/whatsapp/templates", {
        params: {
          ...(entity ? { legalEntityId: entity } : {}),
          ...(branch ? { branchId: branch } : {}),
          skipGlobalContext: true,
        },
      })
      .then((r) => templatesFromResponse(r.data));
  },

  /** One call per legal entity, de-duplicated by template id. */
  listForEntities: async (legalEntityIds: string[], branchId?: string | null) => {
    const ids = [...new Set(legalEntityIds.map((id) => id.trim()).filter(Boolean))];
    if (ids.length <= 1) return whatsappTemplateService.list(ids[0] ?? "", branchId);
    const pages = await Promise.all(ids.map((id) => whatsappTemplateService.list(id, branchId)));
    const byId = new Map<string, WhatsappTemplate>();
    for (const page of pages) {
      for (const row of page) byId.set(row.id, row);
    }
    return [...byId.values()];
  },
};

/**
 * Counts the highest {N} placeholder index inside a template body so the UI
 * can render the right number of parameter inputs. WhatsApp templates use
 * 1-indexed {1}, {2}, … markers; the count returned is the value of the
 * largest index seen (0 if none).
 */
export function countTemplateParams(body: string | null | undefined): number {
  if (!body) return 0;
  let max = 0;
  // Single-curly form after the WhatsAppTemplateService normalises "{{1}}" → "{1}".
  for (const m of body.matchAll(/\{(\d+)\}/g)) {
    const n = Number.parseInt(m[1], 10);
    if (!Number.isNaN(n) && n > max) max = n;
  }
  return max;
}

/** Resolves a dot-path like "contact.name" against the context object. */
function readPath(ctx: ReachoutResolverContext, path: string): string {
  const parts = path.split(".");
  let cur: unknown = ctx as unknown;
  for (const part of parts) {
    if (cur == null || typeof cur !== "object") return "";
    cur = (cur as Record<string, unknown>)[part];
  }
  if (cur == null) return "";
  if (typeof cur === "number" && Number.isFinite(cur)) return String(cur);
  if (typeof cur === "boolean") return cur ? "true" : "false";
  return String(cur);
}

/**
 * Walks a fallback chain of dot-paths and returns the first non-blank
 * resolution. Falls back to the mapping's literal `fallback` if everything
 * else came back empty.
 */
export function resolveTemplateParam(
  mapping: TemplateParamMapping,
  ctx: ReachoutResolverContext,
): string {
  const paths = Array.isArray(mapping.path) ? mapping.path : [mapping.path];
  for (const p of paths) {
    const value = readPath(ctx, p);
    if (value && value.trim().length > 0) return value;
  }
  return mapping.fallback ?? "";
}

/**
 * Resolves every placeholder for a template against the given context. The
 * returned array length matches the placeholder count of the template body
 * — slots without a mapping (or whose mapping resolved to empty) come back
 * as "" so the UI can render them as editable empty inputs.
 */
export function resolveTemplateParams(
  template: WhatsappTemplate,
  ctx: ReachoutResolverContext,
): string[] {
  const slots = countTemplateParams(template.templateBody);
  const mappings = template.paramMappings ?? [];
  return Array.from({ length: slots }, (_, i) => {
    const m = mappings[i];
    return m ? resolveTemplateParam(m, ctx) : "";
  });
}